import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { canAccessRoute } from '../hooks/usePermissionNavigation';

/**
 * No Permission Page
 * Shown when user tries to access a restricted route
 */
const NoPermissionPage = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3,
      }}
    >
      <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
        You do not have permission to access this page. Please contact your administrator if you
        believe this is an error.
      </Typography>
      <Button variant="contained" onClick={() => window.history.back()} sx={{ mt: 2 }}>
        Go Back
      </Button>
    </Box>
  );
};

/**
 * Permission-based route guard
 * Wraps route components and checks permissions before rendering
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Route component to render
 */
const PermissionRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Check if user has permission to access current route
  const hasPermission = canAccessRoute(user, location.pathname);

  if (!hasPermission) {
    return <NoPermissionPage />;
  }

  return children;
};

export default PermissionRoute;
